import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import classNames from "clsx";
import LogoutIcon from "@mui/icons-material/Logout";
import Avatar from "@mui/material/Avatar";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { AuthContext } from "../../contexts/AuthContext";
import { useVideoUploadPopover } from "../../contexts/VideoUploadPopoverContext";
import logo from "../../assets/dronejtek-log.svg";
import { useTranslation } from "react-i18next";
import { DASHBOARD_SIDEBAR_LINKS } from "../../lib/constants/navigation";
import { api } from "../../utils/api";
import { useUserPermissions } from "../../utils/hooks/usePermissions";
import PLATFORM_TYPES_ENUM from "../../lib/enums/platformTypes";
import { API_ROUTES } from "../../routes/apiRoutes";
import LogoutModal from "../Modal/LogoutModal";
import { LanguageDropdown } from "./LanguageDropdown";
import OptimizedImage from "./OptimizedImage";
import SidebarLink from "./SidebarComponents/SidebarLink";
import SidebarLinkSkeleton from "./SidebarComponents/SidebarLinkSkeleton";
import SidebarNavAccordion from "./SidebarComponents/SidebarNavAccordion";
import ReportSidebarLink from "./SidebarComponents/ReportSidebarLink";
import { REPORT_STEP_KEYS } from "./SidebarComponents/sidebarConstants";
import { BsPin, BsFillPinFill } from "react-icons/bs";
import UserWriteModal from "../../components/UserManagement/UserWriteModal";
import { usePinnedState, useSidebarContextStore } from "./sidebarStore";

export default function Sidebar({ navigation }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { setShow } = useVideoUploadPopover();
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout, user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const overviewImageId = searchParams.get("overviewImageId");
  const buildingId = searchParams.get("buildingId");
  const isContextual =
    /^\/dashboard\/buildings?\//.test(location.pathname) ||
    Boolean(overviewImageId) ||
    Boolean(buildingId);
  const mode = isContextual ? "contextual" : "global";
  const [buildingParts, setBuildingParts] = useState([]);
  const [openLogoutModal, setOpenLogoutModal] = useState(false);

  const isPinned = usePinnedState((state) => state.isPinned);
  const togglePinned = usePinnedState((state) => state.togglePinned);
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const hoverDelayRef = useRef(null);
  const isExpanded = isPinned || isHoverExpanded;
  const isCollapsed = !isExpanded;

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(undefined);
  const menuAnchorRef = useRef(null);
  const collapseTimer = useRef(null);
  const sidebarRef = useRef(null);
  const menuPaperRef = useRef(null);

  const scrollRef = useRef(null);
  const getScrollTop = useSidebarContextStore((s) => s.getScrollTop);
  const setScrollTop = useSidebarContextStore((s) => s.setScrollTop);
  const setLastReport = useSidebarContextStore((s) => s.setLastReport);

  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTop = getScrollTop(mode);
    const handler = () => setScrollTop(mode, node.scrollTop);
    node.addEventListener("scroll", handler);
    return () => {
      setScrollTop(mode, node.scrollTop);
      node.removeEventListener("scroll", handler);
    };
  }, [mode, getScrollTop, setScrollTop]);

  useEffect(() => {
    if (isContextual && buildingId) {
      const reportType = searchParams.get("reportType") || "roof";
      setLastReport(buildingId, reportType, location.pathname + location.search);
    }
  }, [isContextual, buildingId, location.pathname, location.search, searchParams, setLastReport]);

  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    const measure = () => {
      if (menuAnchorRef.current) setMenuWidth(menuAnchorRef.current.offsetWidth);
    };
    measure();
    el.addEventListener("transitionend", measure);
    return () => el.removeEventListener("transitionend", measure);
  }, [isExpanded]);

  const cancelCollapse = () => clearTimeout(collapseTimer.current);

  const scheduleCollapse = () => {
    clearTimeout(collapseTimer.current);
    collapseTimer.current = setTimeout(() => {
      if (!isPinned) setIsHoverExpanded(false);
    }, 120);
  };

  const isPointInRegion = (x, y) => {
    return [sidebarRef.current, menuPaperRef.current].some((el) => {
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    });
  };

  const handleRegionLeave = (e) => {
    if (isPinned) return;
    if (typeof e.clientX === "number" && isPointInRegion(e.clientX, e.clientY)) return;
    clearTimeout(hoverDelayRef.current);
    scheduleCollapse();
  };

  const handleMenuButtonClick = () => {
    cancelCollapse();
    if (!isExpanded) return;
    setMenuOpen((prev) => !prev);
  };

  const openMenuHover = () => {
    cancelCollapse();
    if (isExpanded) setMenuOpen(true);
  };

  const handleMouseEnterSidebar = () => {
    cancelCollapse();
    if (isPinned) return;
    hoverDelayRef.current = setTimeout(() => {
      setIsHoverExpanded(true);
    }, 200);
  };

  useEffect(() => {
    return () => {
      clearTimeout(hoverDelayRef.current);
      clearTimeout(collapseTimer.current);
    };
  }, []);

  const handlePinToggle = () => {
    if (!isPinned) setIsHoverExpanded(false);
    togglePinned();
  };

  const { permissions, hasPermission, isLoading: permissionsLoading } = useUserPermissions();

  const filterByPermission = (links) => {
    if (!permissions) return links;
    return links
      .map((link) => {
        if (link.permission && !hasPermission(link.permission)) return null;
        if (link.type === "accordion") {
          const visibleSublinks = link.sublinks.filter(
            (sub) => !sub.permission || hasPermission(sub.permission),
          );
          if (visibleSublinks.length === 0) return null;
          return { ...link, sublinks: visibleSublinks };
        }
        return link;
      })
      .filter(Boolean);
  };

  const isReportNav = navigation?.length > 0 && REPORT_STEP_KEYS.includes(navigation[0]?.key);

  useEffect(() => {
    const path = location.pathname;
    const overviewMatch = path === "/dashboard/buildings/overview";
    const partMatch = /^\/dashboard\/building\/overview\/building-part\//.test(path);
    if (overviewMatch || partMatch) {
      setDropdownOpen(true);
    } else {
      setDropdownOpen(false);
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    setOpenLogoutModal(false);
    sessionStorage.clear();
    setShow(false);
    await logout();
    navigate("/login");
  };

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);

  const platformType = user?.platform_type;
  const isIndependentContractor =
    platformType === PLATFORM_TYPES_ENUM.CONTRACTOR && !user?.is_dependent;

  const handleProfile = async () => {
    setMenuOpen(false);
    if (platformType === PLATFORM_TYPES_ENUM.CONTRACTOR && isIndependentContractor) {
      navigate("contractor/profile");
    } else {
      try {
        const res = await api.get(API_ROUTES.USER_PROFILE);
        setProfileData(res?.data?.data || {});
        setProfileModalOpen(true);
      } catch (err) {
        return;
      }
    }
  };

  const handleCloseProfileModal = () => {
    setProfileData(null);
    setProfileModalOpen(false);
  };

  const userName = user?.first_name || user?.email || "User";
  const userEmail = user?.email || "";
  const userAvatar = user?.avatarUrl || "";
  const sidebarLinks = filterByPermission(navigation || DASHBOARD_SIDEBAR_LINKS);

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get(API_ROUTES.BUILDING_PARTS, { params: { overviewImageId } });
      const data = response?.data?.data;
      setBuildingParts(data || []);
    } catch (error) {
      console.error("Error fetching project data:", error);
    }
  }, [overviewImageId]);

  useEffect(() => {
    if (overviewImageId) {
      fetchData();
    }
  }, [overviewImageId, fetchData]);

  return (
    <div
      style={{
        position: "relative",
        height: "calc(100vh - 24px)",
        margin: "12px",
        flexShrink: 0,
        width: isPinned ? 270 : 72,
        transition: "width 300ms ease-in-out",
        zIndex: 40,
      }}
    >
      <div
        ref={sidebarRef}
        className={classNames(
          "rounded-2xl border border-gray-200 flex flex-col items-center overflow-hidden",
          "transition-[width] duration-300 ease-in-out",
          isReportNav ? "bg-sidebarColor" : "bg-white",
        )}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: isExpanded ? 270 : 72,
          transition: "width 300ms ease-in-out",
          zIndex: 40,
          boxShadow: isExpanded && !isPinned ? "4px 0 24px 0 rgba(0,0,0,0.10)" : "none",
          ...(!isReportNav ? { backgroundColor: "#f4f8f6" } : {}),
        }}
        onMouseEnter={handleMouseEnterSidebar}
        onMouseLeave={handleRegionLeave}
      >
        <div className="relative flex items-center justify-center w-full py-4 min-h-[64px]">
          <Link to="/dashboard" className="flex items-center justify-center">
            <OptimizedImage
              src={logo}
              alt="DroneTjek Logo"
              className="h-10 w-auto cursor-pointer"
            />
          </Link>

          <button
            type="button"
            onClick={handlePinToggle}
            title={isPinned ? "Unpin sidebar" : "Pin sidebar"}
            className="absolute right-2 flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
            style={{
              opacity: isExpanded ? 1 : 0,
              pointerEvents: isExpanded ? "auto" : "none",
              transition: "opacity 200ms ease-in-out",
            }}
          >
            {isPinned ? <BsFillPinFill size={15} /> : <BsPin size={15} />}
          </button>
        </div>

        <div
          key={mode}
          ref={scrollRef}
          className="sidebar-mode-panel flex flex-col items-center gap-4 mt-6 flex-1 w-full overflow-y-auto custom-scrollbar overflow-x-hidden"
        >
          {isReportNav ? (
            <ReportSidebarLink
              sidebarLinks={sidebarLinks}
              isCollapsed={isCollapsed}
              buildingParts={buildingParts}
            />
          ) : permissionsLoading ? (
            Array.from({ length: 3 }, (_, i) => (
              <SidebarLinkSkeleton key={i} isCollapsed={isCollapsed} />
            ))
          ) : (
            sidebarLinks.map((link, index) => {
              switch (link.type) {
                case "accordion":
                  return (
                    <SidebarNavAccordion key={link.key} link={link} isCollapsed={isCollapsed} />
                  );
                default:
                  return (
                    <SidebarLink
                      key={link.key}
                      link={link}
                      index={index}
                      buildingParts={link.key === "custProperty" ? buildingParts : null}
                      dropdownOpen={dropdownOpen}
                      setDropdownOpen={setDropdownOpen}
                      isCollapsed={isCollapsed}
                    />
                  );
              }
            })
          )}
        </div>

        <div className="flex flex-col items-center justify-center mb-3 relative w-full px-2">
          <button
            ref={menuAnchorRef}
            type="button"
            onMouseEnter={cancelCollapse}
            onClick={handleMenuButtonClick}
            className={classNames(
              "flex items-center gap-2 rounded-xl text-sm transition w-full pl-[10px] py-2 mt-2.5",
              !isCollapsed && "pr-3 bg-[#F9FAFB] hover:bg-gray-100 border border-gray-200",
            )}
          >
            <Avatar
              src={
                user?.profile_image
                  ? `${user.profile_image}?v=${user._img_ver || Date.now()}`
                  : userAvatar
              }
              sx={{
                width: 32,
                height: 32,
                bgcolor: "#3D6C50",
                border: "1px solid #E5E7EB",
                flexShrink: 0,
              }}
            >
              {userName ? userName[0] : "U"}
            </Avatar>

            <div
              className="flex flex-col items-start min-w-0 overflow-hidden"
              style={{
                maxWidth: isCollapsed ? 0 : 140,
                opacity: isCollapsed ? 0 : 1,
                visibility: isCollapsed ? "hidden" : "visible",
                transition: isCollapsed
                  ? "max-width 250ms ease-in-out, opacity 100ms ease-in-out"
                  : "max-width 250ms ease-in-out, opacity 200ms ease-in-out 80ms",
              }}
            >
              <span className="text-xs font-medium truncate max-w-[120px] whitespace-nowrap">
                {userName}
              </span>
              <span className="text-xs text-gray-500 truncate max-w-[120px] whitespace-nowrap">
                {userEmail}
              </span>
            </div>
          </button>

          <Menu
            anchorEl={menuAnchorRef.current}
            open={menuOpen && isExpanded}
            onClose={() => setMenuOpen(false)}
            hideBackdrop
            disableScrollLock
            disableAutoFocus
            disableAutoFocusItem
            disableEnforceFocus
            disableRestoreFocus
            autoFocus={false}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            transformOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{ pointerEvents: "none" }}
            PaperProps={{
              ref: menuPaperRef,
              onMouseEnter: openMenuHover,
              onMouseLeave: handleRegionLeave,
              sx: {
                pointerEvents: "auto",
                width: menuWidth,
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                mt: "-5px",
                p: 0.5,
                fontSize: "0.45rem",
                boxShadow: "none",
              },
            }}
            MenuListProps={{ sx: { p: 0, fontSize: "0.85rem" } }}
          >
            <LanguageDropdown />
            <>
              {platformType !== PLATFORM_TYPES_ENUM.ADMIN ? (
                <MenuItem
                  onClick={handleProfile}
                  sx={{
                    borderRadius: 1,
                    px: 2,
                    py: 1.2,
                    fontWeight: 500,
                    fontSize: "0.85rem",
                    color: "#374151",
                    "&:hover": { background: "#f3f4f6" },
                  }}
                >
                  <Avatar
                    src={
                      user?.profile_image
                        ? `${user.profile_image}?v=${user._img_ver || Date.now()}`
                        : userAvatar
                    }
                    sx={{
                      bgcolor: "#3D6C50",
                      border: "1px solid #E5E7EB",
                    }}
                    className="!w-8 !h-8 object-cover mr-1.5"
                  >
                    <AccountCircleIcon sx={{ mr: 1, color: "#6b7280" }} />
                  </Avatar>
                  {t("main.sidebar.profile", "Profile")}
                </MenuItem>
              ) : null}
            </>

            <div style={{ borderTop: "1px solid #e5e7eb" }} />
            <MenuItem
              onClick={() => {
                setMenuOpen(false);
                setOpenLogoutModal(true);
              }}
              sx={{
                borderRadius: 1,
                px: 2,
                py: 1.2,
                fontWeight: 500,
                fontSize: "0.85rem",
                color: "#ef4444",
                "&:hover": { background: "#fef2f2", color: "#b91c1c" },
              }}
            >
              <LogoutIcon sx={{ mr: 1, color: "#ef4444" }} /> {t("main.sidebar.logout", "Logout")}
            </MenuItem>
          </Menu>
        </div>

        {openLogoutModal && (
          <LogoutModal
            isOpen={openLogoutModal}
            onClose={() => setOpenLogoutModal(false)}
            onConfirm={handleLogout}
          />
        )}

        <UserWriteModal
          open={profileModalOpen}
          defaultValues={profileData}
          onClose={handleCloseProfileModal}
        />
      </div>
    </div>
  );
}
